import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  CircularProgress,
  Box,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddCancelDialogButtons from "components/AddCancelDialogButtons";


const AddInitiativeDialog = ({
  open,
  onClose,
  initiative,
  handleSubmit,
  handleDelete,
  companyId,
  jobs,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [initiativeName, setInitiativeName] = useState("");
  const [initiativeType, setInitiativeType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [completionPercentage, setCompletionPercentage] = useState("");
  const [jobOptions, setJobOptions] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [errors, setErrors] = useState({});

  // Update form when initiative prop changes
  useEffect(() => {
    if (initiative) {
      setInitiativeName(initiative.initiativeName || "");
      setInitiativeType(initiative.initiativeType || "");
      setStartDate(initiative.startDate || "");
      setEndDate(initiative.endDate || "");
      setCompletionPercentage(initiative.completionPercentage || "");
      setSelectedJobId(initiative.jobId || null);
    } else {
      // Reset form when adding new initiative
      setInitiativeName("");
      setInitiativeType("");
      setStartDate("");
      setEndDate("");
      setCompletionPercentage("");
      setSelectedJobId(null);
    }
  }, [initiative]);

  useEffect(() => {
    if (!open) return;
    if (jobs) {
      setJobOptions(jobs);
    }
  }, [open, jobs]);

  const validate = () => {
    let tempErrors = {};
    if (!initiativeName) tempErrors.initiativeName = "Required";
    if (!initiativeType) tempErrors.initiativeType = "Required";
    if (!startDate) tempErrors.startDate = "Required";
    if (!endDate) tempErrors.endDate = "Required";
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start > end) {
        tempErrors.endDate = "End date cannot be before start date";
      }
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    const initiativeData = {
      ...initiative,
      initiativeName: initiativeName,
      initiativeType: initiativeType,
      companyId: companyId,
      jobId: selectedJobId, // Store the chosen job ID
      startDate: startDate,
      endDate: endDate,
      completionPercentage: Number(completionPercentage),
    };
    await handleSubmit(initiativeData);
    setLoading(false);
    handleClose();
  };

  const handleClose = () => {
    setInitiativeName("");
    setCompletionPercentage(null);
    setInitiativeType("");
    setStartDate("");
    setEndDate("");
    setErrors({});
    setJobOptions([]);
    setSelectedJobId(null);
    onClose();
  };
  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>
        {initiative
          ? t("admin.strategicInfo.initiatives.editDialogTitle")
          : t("admin.strategicInfo.initiatives.addDialogTitle")}
      </DialogTitle>
      <DialogContent>
        <TextField
          label={t("admin.strategicInfo.initiatives.dialog.nameLabel") + " *"}
          value={initiativeName}
          onChange={(e) => setInitiativeName(e.target.value)}
          fullWidth
          margin="normal"
          error={!!errors.initiativeName}
          helperText={errors.initiativeName}
        />
        <TextField
          label={t("admin.strategicInfo.initiatives.dialog.typeLabel") + " *"}
          value={initiativeType}
          onChange={(e) => setInitiativeType(e.target.value)}
          fullWidth
          margin="normal"
          error={!!errors.initiativeType}
          helperText={errors.initiativeType}
        />
        <TextField
          label={t("admin.strategicInfo.initiatives.dialog.completionLabel")}
          type="number"
          value={completionPercentage}
          onChange={(e) => setCompletionPercentage(e.target.value)}
          fullWidth
          margin="normal"
          inputProps={{ min: 0, max: 100 }}
        />

        <Autocomplete
          options={jobOptions}
          getOptionLabel={(option) => option.jobName || ""}
          // value={jobOptions.find((j) => j.jobId === initiative?.jobId) || null}
          value={jobOptions.find((j) => j.jobId === selectedJobId) || null} // Use state instead of initiative.jobId
          onChange={(event, newValue) => {
            setSelectedJobId(newValue ? newValue.jobId : null); // Update selected job ID
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.strategicInfo.initiatives.dialog.jobTitleLabel")}
              margin="normal"
              fullWidth
            />
          )}
        />
        <TextField
          label={t("admin.strategicInfo.initiatives.dialog.startDateLabel") + " *"}
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          error={!!errors.startDate}
          helperText={errors.startDate}
        />
        <TextField
          label={t("admin.strategicInfo.initiatives.dialog.endDateLabel") + " *"}
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          error={!!errors.endDate}
          helperText={errors.endDate}
        />
      </DialogContent>

      {!loading && (
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            {t("admin.strategicInfo.initiatives.dialog.cancel")}
          </Button>
          {initiative && (
            <Button
              onClick={async () => {
                await handleDelete(initiative?.initiativeId);
                // handleClose();
              }}
              color="error"
              sx={{
                color: "red",
                "&:hover": { backgroundColor: "red", color: "#fff" },
              }}
            >
              {t("admin.strategicInfo.initiatives.dialog.delete")}
            </Button>
          )}
          <Button
            onClick={handleSave}
            color="primary"
            sx={{
              "&:hover": { backgroundColor: "blue", color: "#fff" },
            }}
          >
            {initiative
              ? t("admin.strategicInfo.initiatives.dialog.update")
              : t("admin.strategicInfo.initiatives.dialog.add")}
          </Button>
        </DialogActions>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Dialog>
  );
};

AddInitiativeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initiative: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  companyId: PropTypes.number,
  jobs: PropTypes.array.isRequired,
};

export default AddInitiativeDialog;
